import AccessServiceTileSkeleton from './AccessServiceTileSkeleton';
import { Service } from './types';
import clsx from 'clsx';
import {
  CategorizedOfferings,
  OfferingType,
  ProductCategory,
} from 'screens/AccessExpressV3/accessExpress.types';
import { ProductType as OfferingProductType } from 'screens/SelectServices/schemas';
import GlowingBorder from 'screens/AccessExpressV3/BaseComponents/GlowingBorder';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { DIA_REFETCH_INTERVAL } from '../AccessProductDetails';
import { observer } from 'mobx-react-lite';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import { getQuoteFormalPricing } from 'api/accessexpress/api';
import {
  categorizeOfferings,
  getOfferingPrice,
  getMNSPrice,
} from 'screens/AccessExpressV3/utils';
import {
  LocationType,
  ProductLocationType,
} from 'screens/SelectServices/schemas';
import { groupProducts } from 'screens/SelectServices/utils';
import { formatCurrency } from 'shared/util/formatCurrency';
import AccessServiceTileNotServiceable from './AccessServiceTileNotServiceable';
import ServiceTypeTag, {
  ServiceType,
} from 'screens/AccessExpressV3/BaseComponents/SeviceType';

interface AccessServiceTileProps {
  service: Service;
  selected: boolean;
  // loading: boolean;
  quoteId?: string;
  onSelect: () => void;
}

const AccessServiceTile = observer(
  ({
    service: item,
    selected,
    // loading,
    quoteId,
    onSelect,
  }: AccessServiceTileProps) => {
    const category = item.label as ProductCategory;

    const cart = useCartContext();

    const {
      locations,
      activeLocation,
      loadingDraftForPricing,
      setLoadingDraftQuote,
      setStartPolling,
      startPolling,
      diaFlowActive,
      setDIAFlowActive,
    } = cart;

    const shouldQueryBeEnabled = diaFlowActive
      ? startPolling
      : !loadingDraftForPricing;

    const { data, isLoading } = useQuery(
      ['access_products', quoteId, category],
      () => getQuoteFormalPricing(quoteId!),
      {
        enabled: !!quoteId && !!locations.length && shouldQueryBeEnabled,
        refetchInterval: (data) => {
          if (category !== 'DIA') return false;
          if (
            data?.quote_status === 'Closed' ||
            data?.quote_status === 'Order services'
          ) {
            return false; // stop polling
          }
          return DIA_REFETCH_INTERVAL; // keep polling otherwise
        },
        retry: false,
        staleTime: DIA_REFETCH_INTERVAL,
        onSettled: (data, error) => {
          if (
            data?.locations?.length ||
            data?.quote_status === 'Closed' ||
            data?.quote_status === 'Order services' ||
            error
          ) {
            setDIAFlowActive(false);
            setStartPolling(false);
            setLoadingDraftQuote(false);
          }
        },
      },
    );

    const currentAddress = activeLocation;

    const currentLocation: ProductLocationType = data?.locations?.find(
      (loc: LocationType & { external_id: string }) =>
        loc.external_id === currentAddress?.external_id,
    );

    const products = useMemo(
      () => groupProducts(data?.locations || []) || [],
      [data?.locations],
    );

    const flattenedProducts = useMemo(() => products.flat(), [products]);

    const locationProducts = useMemo(
      () =>
        flattenedProducts?.find(
          ({ external_id }) => external_id === currentLocation?.external_id,
        ),
      [currentLocation, flattenedProducts],
    );

    const {
      bestOffer,
      bestOfferDetails,
      premiumChoiceDetails,
      premiumChoice,
      lowestPrice,
      lowestPriceDetails,
      otherOption,
      otherOptionDetails,
    } = categorizeOfferings(
      category as ProductCategory,
      //@ts-expect-error err
      locationProducts as unknown as AccessProductType,
    ) as CategorizedOfferings;

    const isStarlink = category === 'Starlink';
    const summaryData = [
      [bestOfferDetails, bestOffer],
      ...(isStarlink && bestOffer && otherOption
        ? [[otherOptionDetails, otherOption]]
        : [
            [premiumChoiceDetails, premiumChoice],
            [lowestPriceDetails, lowestPrice],
          ]),
    ] as [OfferingType, OfferingProductType][];
    const service = {
      label: item.label,
      offers: summaryData.map((offering) => {
        const price =
          getOfferingPrice(offering?.[1]) + getMNSPrice(offering?.[1], true);

        return {
          id: offering?.[1]?.id,
          price: formatCurrency(price || 0),
          bandwidth:
            offering?.[0]?.details?.find((item) => item.id === 'data_plan')
              ?.text ||
            offering?.[0]?.details?.find((item) => item.id === 'speed')?.text,
          carrier: '',
        };
      }),
    };

    const isCheckingAvailability =
      data?.quote_status === 'Checking availability';

    const isNotServiceable =
      !currentLocation ||
      currentLocation?.formal_products_offerings.length === 0;

    return (
      <div
        onClick={onSelect}
        className={clsx(
          'group relative min-h-[124px] w-[272px] rounded-lg shadow-elevation2 transition-colors duration-200 ease-in-out',
          !selected && 'cursor-pointer',
          'border border-stroke-base-subdued',
        )}
      >
        <span
          className={clsx(
            'pointer-events-none absolute -inset-px hidden group-hover:block',
            selected && '!block',
          )}
        >
          <GlowingBorder borderWidth={1} borderRadius={8} />
        </span>
        {selected && (
          <div className="pointer-events-none absolute inset-0 z-10 hidden flex-col items-center justify-center gap-2.5 rounded-2xl bg-background-base-surface-1/80 backdrop-blur-sm group-hover:flex">
            <span className="text-xs font-medium text-content-base-subdued">
              Currently viewing
            </span>
          </div>
        )}

        <div
          className={clsx(
            'flex h-full flex-col',
            selected &&
              'rounded-lg bg-gradient-to-b from-background-ae-product-all-servic-tile-from to-background-ae-product-all-servic-tile-to',
          )}
        >
          {isLoading ||
          loadingDraftForPricing ||
          isCheckingAvailability ||
          !quoteId ? (
            <AccessServiceTileSkeleton
              category={service.label as ProductCategory}
            />
          ) : isNotServiceable ? (
            <AccessServiceTileNotServiceable
              category={service.label as ProductCategory}
              isActive={selected}
            />
          ) : (
            <div className="flex h-full flex-col">
              <header
                className={clsx(
                  'relative flex min-h-[64px] flex-col items-start gap-1 rounded-t-2xl p-3 pb-0',
                  service.offers.length > 1
                    ? 'border-b border-background-base-surface-3'
                    : 'h-[112px] justify-center',
                )}
              >
                {service.offers.length === 1 && (
                  <span
                    className={clsx(
                      'text-sm font-bold',
                      selected
                        ? 'text-content-base-default'
                        : 'text-content-base-subdued',
                    )}
                  >
                    {service.offers[0].carrier}
                  </span>
                )}
                <span
                  className={clsx(
                    'flex flex-row items-center gap-2 font-bold group-hover:text-content-base-default',
                    service.offers.length > 1
                      ? 'text-sm'
                      : 'text-[28px] leading-[36px]',
                    selected
                      ? 'text-content-base-default'
                      : 'text-content-base-subdued',
                  )}
                >
                  {service.offers[0].price}
                  {service.offers.length === 1 && (
                    <span className="text-base font-bold text-content-base-subdued">
                      /month
                    </span>
                  )}
                </span>
                <span className="whitespace-nowrap text-xs font-medium text-content-base-subdued">
                  {service.offers[0].bandwidth}
                </span>
                <span
                  className={clsx(
                    !selected && 'opacity-50',
                    'absolute right-3 top-3 group-hover:!opacity-100',
                  )}
                >
                  <ServiceTypeTag
                    type={category as ServiceType}
                    className="ml-4 max-h-6 w-[85px]"
                    labelClassName="whitespace-nowrap"
                  />
                </span>
              </header>

              {service.offers.length > 1 && (
                <ul className="flex flex-1 divide-x divide-background-base-surface-3">
                  {service.offers.slice(1).map((offer) => (
                    <li
                      key={offer.id}
                      className="flex flex-1 flex-col items-start gap-1 p-3 pb-0"
                    >
                      <span
                        className={clsx(
                          'text-xs font-bold group-hover:text-content-base-default',
                          selected
                            ? 'text-content-base-default'
                            : 'text-content-base-subdued',
                        )}
                      >
                        {offer.price}
                      </span>
                      <span className="whitespace-nowrap text-[10px] font-semibold text-content-base-subdued">
                        {offer.bandwidth}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default AccessServiceTile;
